import React, { useMemo } from 'react';
import styled from 'styled-components';
import { Link, useLocation } from 'react-router-dom';
import { getAppProjectBySlug } from '../../data/apps';
import { useLanguage } from '../../i18n';

const Page = styled.div`
  padding-top: 110px;
  padding-bottom: 80px;
  background: ${props => props.theme.colors.background};
`;

const Container = styled.div`
  max-width: 960px;
  margin: 0 auto;
  padding: 0 24px;
`;

const Title = styled.h1`
  font-size: 34px;
  font-weight: 900;
  color: ${props => props.theme.colors.textDark};
  margin-bottom: 10px;
`;

const Meta = styled.p`
  color: ${props => props.theme.colors.textLight};
  margin-bottom: 26px;
`;

const Card = styled.div`
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.xl};
  padding: 22px;
  background: ${props => props.theme.colors.white};
  box-shadow: ${props => props.theme.shadows.sm};
`;

const H2 = styled.h2`
  font-size: 16px;
  font-weight: 900;
  margin: 18px 0 10px;
  color: ${props => props.theme.colors.textDark};
`;

const P = styled.p`
  line-height: 1.85;
  color: ${props => props.theme.colors.text};
  margin: 10px 0;
`;

const List = styled.ul`
  margin: 8px 0 8px 18px;
  line-height: 1.85;
  color: ${props => props.theme.colors.text};
`;

function useQuery() {
  const location = useLocation();
  return useMemo(() => new URLSearchParams(location.search), [location.search]);
}

const PrivacyPolicyPage: React.FC = () => {
  const { t } = useLanguage();
  const query = useQuery();
  const slug = query.get('app') ?? undefined;
  const app = getAppProjectBySlug(slug);
  const updatedAt = '2024-07-01';
  const effectiveAt = '2024-07-01';

  return (
    <Page>
      <Container>
        <Title>{t('legal.privacy')}</Title>
        <Meta>
          {app ? (
            <>
              {t('legal.appliesTo')}: <strong>{app.name}</strong> ·{' '}
            </>
          ) : null}
          更新日期：{updatedAt} · 生效日期：{effectiveAt} · <Link to="/">{t('legal.backHome')}</Link>
        </Meta>

        <Card>
          <P>
            欢迎你使用「熊猫陪伴（PandaSpeak）」（以下简称“本应用”）。我们重视你的隐私与个人信息保护。本政策用于说明我们如何收集、使用、共享、存储与保护信息，以及你享有的权利。
          </P>

          <H2>1. 我们收集的信息</H2>
          <List>
            <li>你主动提供的内容：例如你输入的文本、上传的图片、语音对话内容（用于实现对话/讲故事/相关功能）。</li>
            <li>订阅相关信息：我们会通过 Apple 的订阅体系识别你的订阅状态（如是否订阅、订阅类型/到期状态）。我们不会获取你的银行卡等支付信息。</li>
            <li>设备与诊断信息：例如设备型号、系统版本、崩溃日志、性能数据，用于保障稳定性与排查问题。</li>
          </List>

          <H2>2. 我们如何使用信息</H2>
          <List>
            <li>提供与维护核心功能（语音互动、内容生成/展示、订阅权益解锁等）。</li>
            <li>改进产品体验（优化性能、修复 bug、防止滥用与保障安全）。</li>
            <li>在符合法律法规的前提下履行合规义务、处理争议与投诉。</li>
          </List>

          <H2>3. 信息共享与第三方服务</H2>
          <P>
            我们不会出售你的个人信息。为实现功能与提供服务，我们可能会使用必要的第三方服务（例如云服务、分析/崩溃统计、AI 推理服务等），并在业务所必需范围内共享最少信息，同时采取合理措施保护数据安全。
          </P>

          <H2>4. 信息存储与安全</H2>
          <List>
            <li>我们会在实现服务目的所必需的期限内保存信息，或根据法律要求保存。</li>
            <li>我们采用合理的技术与管理措施（如访问控制、传输加密等）保护信息安全。</li>
          </List>

          <H2>5. 你的权利</H2>
          <List>
            <li>你可以通过系统设置管理权限（如麦克风/相册等），拒绝授权可能影响部分功能使用。</li>
            <li>你可以随时在 iOS 的订阅管理中查看、取消订阅。</li>
            <li>
              如需访问、更正、删除相关信息或提出疑问，可通过邮箱联系：{' '}
              <a href="mailto:xbmld1@gmail.com">xbmld1@gmail.com</a>。
            </li>
          </List>

          <H2>6. 未成年人保护</H2>
          <P>若你未满法定成年年龄，请在监护人同意与指导下使用本应用。</P>

          <H2>7. 政策更新</H2>
          <P>
            我们可能会不时更新本政策。更新后将发布在本页面并注明更新日期。若变更对你的权益产生重大影响，我们会以合理方式提示你。
          </P>

          <P style={{ marginTop: 16 }}>
            <Link to="/terms">{t('legal.terms')}</Link>
          </P>
        </Card>
      </Container>
    </Page>
  );
};

export default PrivacyPolicyPage;

